import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, of } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { HandoffInfo } from 'src/app/common/models/handoffInfo';
import { IFacility } from 'src/app/common/models/facility';
import { Operation } from 'src/app/common/models/operation';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { StoreHandoffService } from 'src/app/services/store-handoff.service';
import { FacilitiesService } from 'src/app/services/facilities.service';

import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MenuGroupComponent } from './menu-group/menu-group.component';
import { MenuItemComponent } from './menu-group/menu-item/menu-item.component';

@Component({
  selector: 'app-sidebar-navigation',
  templateUrl: './sidebar-navigation.component.html',
  styleUrls: ['./sidebar-navigation.component.scss'],
  standalone: true,
  imports: [MatSlideToggle, MenuGroupComponent, MenuItemComponent],
})
export class SidebarNavigationComponent implements OnInit, OnDestroy {
  private handoffInfo: HandoffInfo[] = [];
  public currentFacilityId: string;
  public facilityStatus = false;
  public displayHolidayOrders = false;
  public authManager: AuthManager;
  private readonly destroy$ = new Subject<void>();

  public get currentFacilityName(): string {
    return this.getFacilityById(this.currentFacilityId)?.facilityName ?? 'Wegmans';
  }

  constructor(
    private authorizationServiceDI: AuthManager,
    private toastr: ToastrService,
    private handoffService: StoreHandoffService,
    private _facilityManager: FacilityManager,
    private readonly facilitiesService: FacilitiesService,
  ) {
    this.authManager = this.authorizationServiceDI;

    this.currentFacilityId = this._facilityManager.getCurrentFacility();
  }

  public ngOnInit(): void {
    // wait for auth to finish
    this.authManager.isLoggedIn$
      .pipe(
        filter((isAuthed) => isAuthed),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // sub to facility changes and update the facility status and displayHolidayOrders status accordingly
        this._facilityManager.facilityIdUpdates$
          .pipe(
            takeUntil(this.destroy$),
            switchMap((facilityId) => {
              this.currentFacilityId = facilityId;
              this.facilityStatus = this.getFacilityStatusById(facilityId);
              this.displayHolidayOrders = false;

              if (!facilityId || facilityId === '0') {
                return of(null);
              }

              if (!this.authManager.doesCurrentAccountHaveAnyRole(['IT', 'Admin'])) {
                return of(null);
              }

              return this.facilitiesService.getFacilityById(+facilityId).pipe(
                catchError(() => {
                  this.toastr.error('Unable to load facility holiday orders display status');
                  return of(null);
                }),
              );
            }),
          )
          .subscribe({
            next: (facility: IFacility | null) => {
              if (facility) {
                this.displayHolidayOrders = facility.displayHolidayOrders;
              }
            },
          });

        // sub to all handoffInfo changes
        this.handoffService.handoffInformationCacheUpdates.pipe(takeUntil(this.destroy$)).subscribe((handoffInfo) => {
          this.handoffInfo = handoffInfo;
          this.facilityStatus = this.getFacilityStatusById(this.currentFacilityId);
        });
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getFacilityById(facilityId: string): HandoffInfo | undefined {
    return this.handoffInfo.find((f) => f.facilityId.toString() === facilityId);
  }

  private getFacilityStatusById(facilityId: string): boolean {
    return this.getFacilityById(facilityId)?.isAvailable ?? false;
  }

  public updateStoreAvailability(): void {
    // flip the facility status
    this.facilityStatus = !this.facilityStatus;

    const facilityIdAtRequest = this.currentFacilityId;
    const valueAtRequest = this.facilityStatus;

    const patch: Operation[] = [
      {
        op: 'replace',
        path: '/isAvailable',
        value: this.facilityStatus,
      },
    ];

    // patch the facility
    this.handoffService.upsertStoreHandoffInformation(this.currentFacilityId, patch).subscribe({
      next: () => {
        return; // no-op on success
      },
      error: (err) => {
        if (this.currentFacilityId == facilityIdAtRequest) {
          this.facilityStatus = !valueAtRequest;
        }
        this.toastr.error(
          'An error occurred while updating Facility ['
            + this.currentFacilityId
            + ' - '
            + this.currentFacilityName
            + ']: '
            + err,
        );
      },
    });
  }

  public updateHolidayOrdersDisplay(): void {
    // flip the displayHolidayOrders status
    this.displayHolidayOrders = !this.displayHolidayOrders;

    const facilityIdAtRequest = this.currentFacilityId;
    const valueAtRequest = this.displayHolidayOrders;

    const patch: Operation[] = [
      {
        op: 'replace',
        path: '/displayHolidayOrders',
        value: this.displayHolidayOrders,
      },
    ];

    // patch the facility
    this.facilitiesService.updateFacility(this.currentFacilityId, patch).subscribe({
      next: () => {
        return; // no-op on success
      },
      error: (err) => {
        if (this.currentFacilityId == facilityIdAtRequest) {
          this.displayHolidayOrders = !valueAtRequest;
        }
        this.toastr.error(
          'An error occurred while updating holiday orders display status for ['
            + this.currentFacilityId
            + ' - '
            + this.currentFacilityName
            + ']: '
            + err,
        );
      },
    });
  }
}
